import {otsBaseApi} from "../../ots-api";
import {
  NotificationSubscription,
  ResolvedNotificationSubscription,
  UserNotificationSave,
  UserNotificationSubscription
} from "../../../model/notification-subscription";
import {removeUndefined} from "../../../util/util";

const ALL_TAG_TYPES = [
  "NotificationSubscriptions",
  "UserNotificationSubscriptions",
  "ObjectObjectChangeLogs",
  "ManagedContainerStatusSubscriptions",
] as const;

type TagType = typeof ALL_TAG_TYPES[number];

const taggedBaseApi = otsBaseApi.enhanceEndpoints({
  addTagTypes: ALL_TAG_TYPES
});

const notificationSubscriptionApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllNotificationSubscriptions: builder.query<
      NotificationSubscription[],
      void
    >({
      query: () => ({
        url: `/notification-subscriptions/all`,
        method: 'GET',
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "NotificationSubscriptions", id} as const)),
            {type: "NotificationSubscriptions", id: "LIST"}
          ];
        } else {
          return [{type: "NotificationSubscriptions", id: "LIST"}];
        }
      },
    }),
    getUserNotificationSubscriptions: builder.query<
      UserNotificationSubscription[],
      string | undefined
    >({
      query: (arg) => ({
        url: `/notification-subscriptions`,
        method: 'GET',
        params: removeUndefined({
          personId: arg
        })
      }),
      providesTags: (result, error, arg) => {
        if (result) {
          return [
            {type: "UserNotificationSubscriptions", id: arg ? arg : "LIST"}
          ];
        } else {
          return [{type: "UserNotificationSubscriptions", id: arg ? arg : "LIST"}];
        }
      },
    }),
    getNotificationSubscription: builder.query<NotificationSubscription, string>({
      query: (id) => ({
        url: `/notification-subscriptions/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{type: "NotificationSubscriptions", id}]
    }),
    createNotificationSubscription: builder.mutation<NotificationSubscription, Partial<NotificationSubscription>>({
      query: (mapping) => ({
        url: "/notification-subscriptions",
        method: "POST",
        body: mapping
      }),
      invalidatesTags: [{type: "NotificationSubscriptions", id: "LIST"}]
    }),
    updateNotificationSubscription: builder.mutation<NotificationSubscription, Partial<NotificationSubscription>>({
      query: (mapping) => ({
        url: "/notification-subscriptions",
        method: "PUT",
        body: mapping,
      }),
      invalidatesTags: (result, error, {id}) => [
        {type: "NotificationSubscriptions", id},
        {type: "ObjectObjectChangeLogs", id}
      ]
    }),
    deleteNotificationSubscription: builder.mutation<void, Partial<NotificationSubscription>>({
      query: (mapping) => ({
        url: "/notification-subscriptions",
        method: "DELETE",
        body: mapping
      }),
      invalidatesTags: (result, error, {id}) => [{type: "NotificationSubscriptions", id}]
    }),
    saveUserNotificationSubscription: builder.mutation<ResolvedNotificationSubscription, {
      templateId: string,
      dto: UserNotificationSave
    }>({
      query: ({templateId, dto}) => ({
        url: `/notification-subscriptions/save/${templateId}`,
        method: "POST",
        body: dto
      }),
      invalidatesTags: (result, _error, arg) => {
        const tags: { type: TagType, id?: string | number }[] = [];

        tags.push({type: "UserNotificationSubscriptions", id: arg.dto.personId ? arg.dto.personId : "LIST"})

        if (result) {
          tags.push({type: "NotificationSubscriptions", id: result.id})
        } else if (arg.dto.id) {
          tags.push({type: "NotificationSubscriptions", id: arg.dto.id})
        }

        tags.push({ type: "ManagedContainerStatusSubscriptions" });
        return tags
      },
    }),
  })
});

export const {
  useGetAllNotificationSubscriptionsQuery,
  useGetUserNotificationSubscriptionsQuery,
  useGetNotificationSubscriptionQuery,
  useCreateNotificationSubscriptionMutation,
  useUpdateNotificationSubscriptionMutation,
  useDeleteNotificationSubscriptionMutation,
  useSaveUserNotificationSubscriptionMutation,
} = notificationSubscriptionApi;
